/**
 * A parameter's key read as words where no label is at hand (the 2026-10-24 sweep, items 13.2 and 14.3): separators as
 * spaces, and the short forms a plant manager would not read at a glance spelled out as the machine page spells them
 * (platform-core entity_port_label.go, portShortForms): "afs_grain_fineness" reads "AFS grain fineness",
 * "core_temp_in" reads "core temperature in". The case of every other word is kept. Pure.
 */

/** Short forms in a key, by the word in lower case, as they are written out. */
export const SHORT_FORMS: Readonly<Record<string, string>> = {
  afs: "AFS",
  evt: "event",
  loi: "loss on ignition",
  ph: "pH",
  temp: "temperature",
};

/** What follows a second probe's parameter in words: never its tag's id (#1696). */
export const SECOND_PROBE_WORDS = " (second probe)";

/** A tag's id as the platform writes it after a second probe's parameter (heat_port_series.go, probeParamName). */
const PROBE_ID = /@[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A key split from a second probe's tag id: "bath_temperature@<id>" is the name it shares, probe true; any other key as
 * it is, probe false.
 */
export function probeOf(key: string): { key: string; probe: boolean } {
  const m = PROBE_ID.exec(key);
  return m && m.index > 0 ? { key: key.slice(0, m.index), probe: true } : { key, probe: false };
}

/** words of a key, a second probe's read as words of the name it shares and "(second probe)". */
export function withProbeWords(key: string, words: (key: string) => string): string {
  const p = probeOf(key);
  return p.probe ? words(p.key) + SECOND_PROBE_WORDS : words(key);
}

/** A key's words: "core_temp_in" is "core temperature in". */
export function paramKeyWords(key: string): string {
  return withProbeWords(key, (k) =>
    k
      .split(/[_.\s]+/)
      .filter(Boolean)
      .map((w) => SHORT_FORMS[w.toLowerCase()] ?? w)
      .join(" "),
  );
}

/** A parameter's name as a trace chip shows it, its underscores as spaces: "core_temp_in" is "core temp in". */
export function paramNameWords(name: string): string {
  return withProbeWords(name, (k) => k.replace(/_/g, " "));
}
